import { z } from 'zod';

export const QuerySortSchema = z.object({
  field: z.string().min(1),
  direction: z.enum(['asc', 'desc']),
});

export const QueryFilterSchema = z.object({
  field: z.string().min(1),
  operator: z.enum([
    'eq',
    'neq',
    'contains',
    'startsWith',
    'endsWith',
    'in',
    'gt',
    'lt',
    'gte',
    'lte',
  ]),
  value: z.unknown(),
});

export const MAX_PAGE_SIZE = 100;

export const QueryRequestSchema = z.object({
  page: z.number().int().positive(),
  pageSize: z
    .number()
    .int()
    .positive()
    .max(MAX_PAGE_SIZE, { message: `Pagination cap exceeded (max ${MAX_PAGE_SIZE})` }),
  sorts: z.array(QuerySortSchema).optional(),
  filters: z.array(QueryFilterSchema).optional(),
  search: z.string().optional(),
  includeDeleted: z.boolean().optional(),
  include: z.record(z.string(), z.unknown()).optional(),
});

export type QuerySort = z.infer<typeof QuerySortSchema>;
export type QueryFilter = z.infer<typeof QueryFilterSchema>;
export type QueryRequest = z.infer<typeof QueryRequestSchema>;

export type FilterFieldConfig = {
  operators: ReadonlyArray<QueryFilter['operator']>;
  value: z.ZodType;
};

export const buildFilterSchema = (
  config: Record<string, FilterFieldConfig>,
): z.ZodType<QueryFilter> => {
  const schemas = Object.entries(config).map(([field, cfg]) =>
    z.object({
      field: z.literal(field),
      operator: z.enum(
        cfg.operators as [QueryFilter['operator'], ...Array<QueryFilter['operator']>],
      ),
      value: cfg.value,
    }),
  );

  if (schemas.length === 1) {
    return schemas[0];
  }

  return z.union(
    schemas as unknown as [
      z.ZodType<QueryFilter>,
      z.ZodType<QueryFilter>,
      ...Array<z.ZodType<QueryFilter>>,
    ],
  );
};

export type QueryResult<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};
